import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  LayoutDashboard,
  FileText,
  Users,
  Package,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Building2,
  Receipt,
  Coins,
  Wallet,
  ArrowDownToLine,
  Inbox,
  FileSpreadsheet,
  Truck,
  Warehouse,
  Trash,
  FileCheck2
} from 'lucide-react';
import SubscriptionBadge from './SubscriptionBadge';
import PricingNoticeModal, { SNOOZE_KEY } from './PricingNoticeModal';
import WhatsNewModal, { hasUnseenVersion } from './WhatsNewModal';
import VersionWatcher from './VersionWatcher';
import { APP_VERSION } from '../changelog';
import { Sparkles } from 'lucide-react';
import { useInbox } from '../context/InboxContext';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { pendingCount } = useInbox();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = React.useState(false);
  const [hasUnseen, setHasUnseen] = React.useState(false);
  const [noticeDone, setNoticeDone] = React.useState(false);

  React.useEffect(() => {
    // Marker visivo "NEW" accanto al numero di versione, aggiornato all'avvio.
    setHasUnseen(hasUnseenVersion());
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem(SNOOZE_KEY);
    logout();
    navigate('/login');
  };

  const isSuperAdmin = user?.role === 'super_admin';

  // Super admin sees only the admin panel; company users see all company pages
  const navItems = isSuperAdmin
    ? [
        { to: '/admin', icon: Shield, label: 'Amministrazione' },
      ]
    : [
        { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/inbox', icon: Inbox, label: 'Inbox DDT' },
        { to: '/ddt', icon: FileText, label: 'DDT' },
        { to: '/preventivi', icon: FileSpreadsheet, label: 'Preventivi' },
        ...(user?.is_slave ? [] : [{ to: '/fatture', icon: FileCheck2, label: 'Fatture' }]),
        { to: '/clients', icon: Users, label: 'Clienti' },
        { to: '/products', icon: Package, label: 'Prodotti' },
        { to: '/fornitori', icon: Truck, label: 'Fornitori' },
        { to: '/acquisti', icon: Warehouse, label: 'DDT Acquisto' },
        { to: '/statement', icon: Receipt, label: 'Estratto Conto' },
        { to: '/movimenti', icon: Coins, label: 'Movimenti' },
        { to: '/uscite', icon: Wallet, label: 'Uscite' },
        { to: '/entrate', icon: ArrowDownToLine, label: 'Entrate' },
        { to: '/cestino', icon: Trash, label: 'Cestino' },
        { to: '/settings', icon: Settings, label: 'Impostazioni' },
      ];

  const NavItem = ({ to, icon: Icon, label, badge }) => (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={() => setSidebarOpen(false)}
      data-testid={`nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors rounded-sm ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        }`
      }
    >
      <span className="relative inline-flex">
        <Icon className="w-5 h-5" />
        {badge > 0 && (
          <span
            data-testid="inbox-badge-count"
            className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold leading-none shadow-[0_0_0_2px_var(--card)] animate-pulse"
            aria-label={`${badge} nuovi DDT in attesa`}
          >
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </span>
      <span>{label}</span>
    </NavLink>
  );

  const displayName = user?.full_name || user?.email || '';

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 bg-card border-b border-border flex items-center justify-between px-4">
        <h1 className="text-lg font-bold font-['Manrope'] tracking-tight">DDT MANAGER</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full min-h-0">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-border">
            <h1 className="text-xl font-extrabold font-['Manrope'] tracking-tighter">
              DDT MANAGER
            </h1>
          </div>

          {/* Company / role indicator */}
          {user?.company_name && (
            <div className="px-6 py-3 border-b border-border bg-muted/20">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-xs font-medium truncate" data-testid="layout-company-name">{user.company_name}</span>
              </div>
            </div>
          )}
          {isSuperAdmin && (
            <div className="px-6 py-3 border-b border-border bg-primary/5">
              <Badge variant="default" className="text-[10px]" data-testid="layout-superadmin-badge">
                <Shield className="w-3 h-3 mr-1" /> SUPER ADMIN
              </Badge>
            </div>
          )}

          {/* Navigation — overflow-y-auto per essere sicuri che tutti i link (e il tasto Esci più in basso) siano raggiungibili anche su schermi bassi (es. laptop 720p) */}
          <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => (
              <NavItem
                key={item.to}
                {...item}
                badge={item.to === '/inbox' ? pendingCount : 0}
              />
            ))}
          </nav>

          {/* User section — sticky in fondo per essere sempre visibile */}
          <div className="p-4 border-t border-border bg-card">
            {!isSuperAdmin && <SubscriptionBadge user={user} />}
            <div className="mb-2">
              <p className="text-sm font-medium truncate" data-testid="layout-user-name">
                {displayName}
              </p>
              {user?.full_name && (
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="w-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
            <button
              type="button"
              onClick={() => { setWhatsNewOpen(true); setHasUnseen(false); }}
              data-testid="whats-new-btn"
              className="mt-2 w-full flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors group"
              aria-label="Mostra novità di questa versione"
            >
              <Sparkles className="w-3 h-3 text-amber-500 group-hover:scale-110 transition-transform" />
              <span className="font-mono" data-testid="app-version">v{APP_VERSION}</span>
              <span>· Novità</span>
              {hasUnseen && (
                <span
                  data-testid="whats-new-dot"
                  className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"
                  aria-label="Nuova versione disponibile"
                />
              )}
            </button>
            <p className="text-[10px] text-center text-muted-foreground mt-1">
              Realizzato da <span className="font-semibold">Fahd Hariri</span>
            </p>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      {/* Avviso al login: nuovo canone 400€ + novità fatturazione (Accetta / Ricordamelo dopo) */}
      <PricingNoticeModal user={user} onDone={() => setNoticeDone(true)} />

      {/* Rileva nuove versioni del backend (post-deploy) e offre un reload */}
      <VersionWatcher />

      {/* Modal "Novità versione": auto-open se non ancora vista, oppure manuale dal footer sidebar */}
      {noticeDone && <WhatsNewModal
        open={whatsNewOpen}
        onClose={() => { setWhatsNewOpen(false); setHasUnseen(false); }}
        autoOpen
        user={user}
      />}
    </div>
  );
};

export default Layout;
